/** Logical preference id for sidebar disclosure folder open/closed state. */
export const SIDEBAR_FOLDER_PREFERENCE_KEY = 'sidebar.folder_state'

/** Legacy global key (pre per-user persistence). */
export const LEGACY_SIDEBAR_FOLDER_STORAGE_KEY = 'sidebar_folder_state'

const STORAGE_KEY_PREFIX = 'rukh:sidebar:folder_state:'

export const SIDEBAR_FOLDER_KEYS = [
  'crm',
  'design',
  'pdq',
  'reporting',
  'pim',
  'install',
  'surveys',
  'inquiries',
  'machines',
  'salesHistory',
  'orderEntry',
  'admin',
  'operationsReports',
  'projectManagement',
  'salesDashboard',
  'salesAdmin',
  'salesWorkflow',
  'orderRouting',
  'orderIntake',
  'productRequests',
  'installRequests',
  'designDevelopment',
] as const

export type SidebarFolderKey = (typeof SIDEBAR_FOLDER_KEYS)[number]
export type SidebarFolderState = Record<SidebarFolderKey, boolean>

export function getSidebarFolderStorageKey(userUuid: string): string {
  return `${STORAGE_KEY_PREFIX}${userUuid}`
}

export function createDefaultSidebarFolderState(): SidebarFolderState {
  return SIDEBAR_FOLDER_KEYS.reduce((state, key) => {
    state[key] = false
    return state
  }, {} as SidebarFolderState)
}

function parseSidebarFolderState(raw: string | null): SidebarFolderState | null {
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object') return null

    const defaults = createDefaultSidebarFolderState()
    return {
      crm: typeof parsed.crm === 'boolean' ? parsed.crm : defaults.crm,
      design: typeof parsed.design === 'boolean' ? parsed.design : defaults.design,
      pdq: typeof parsed.pdq === 'boolean'
        ? parsed.pdq
        : typeof parsed.printBridge === 'boolean'
          ? parsed.printBridge
          : defaults.pdq,
      reporting: typeof parsed.reporting === 'boolean' ? parsed.reporting : defaults.reporting,
      pim: typeof parsed.pim === 'boolean' ? parsed.pim : defaults.pim,
      install: typeof parsed.install === 'boolean' ? parsed.install : defaults.install,
      surveys: typeof parsed.surveys === 'boolean' ? parsed.surveys : defaults.surveys,
      inquiries: typeof parsed.inquiries === 'boolean' ? parsed.inquiries : defaults.inquiries,
      machines: typeof parsed.machines === 'boolean' ? parsed.machines : defaults.machines,
      salesHistory: typeof parsed.salesHistory === 'boolean' ? parsed.salesHistory : defaults.salesHistory,
      orderEntry: typeof parsed.orderEntry === 'boolean' ? parsed.orderEntry : defaults.orderEntry,
      admin: typeof parsed.admin === 'boolean' ? parsed.admin : defaults.admin,
      operationsReports: typeof parsed.operationsReports === 'boolean'
        ? parsed.operationsReports
        : defaults.operationsReports,
      projectManagement: typeof parsed.projectManagement === 'boolean'
        ? parsed.projectManagement
        : defaults.projectManagement,
      salesDashboard: typeof parsed.salesDashboard === 'boolean'
        ? parsed.salesDashboard
        : typeof parsed.dashboardPages === 'boolean'
          ? parsed.dashboardPages
          : defaults.salesDashboard,
      salesAdmin: typeof parsed.salesAdmin === 'boolean'
        ? parsed.salesAdmin
        : typeof parsed.dpAdmin === 'boolean'
          ? parsed.dpAdmin
          : defaults.salesAdmin,
      salesWorkflow: typeof parsed.salesWorkflow === 'boolean'
        ? parsed.salesWorkflow
        : defaults.salesWorkflow,
      orderRouting: typeof parsed.orderRouting === 'boolean'
        ? parsed.orderRouting
        : defaults.orderRouting,
      orderIntake: typeof parsed.orderIntake === 'boolean'
        ? parsed.orderIntake
        : defaults.orderIntake,
      productRequests: typeof parsed.productRequests === 'boolean'
        ? parsed.productRequests
        : defaults.productRequests,
      installRequests: typeof parsed.installRequests === 'boolean'
        ? parsed.installRequests
        : defaults.installRequests,
      designDevelopment: typeof parsed.designDevelopment === 'boolean'
        ? parsed.designDevelopment
        : defaults.designDevelopment,
    }
  } catch {
    return null
  }
}

function readLegacySidebarFolderPreference(): SidebarFolderState | null {
  if (typeof localStorage === 'undefined') return null

  try {
    return parseSidebarFolderState(localStorage.getItem(LEGACY_SIDEBAR_FOLDER_STORAGE_KEY))
  } catch {
    return null
  }
}

export function readSidebarFolderPreference(
  userUuid: string | undefined
): SidebarFolderState | null {
  if (!userUuid || typeof localStorage === 'undefined') {
    return null
  }

  try {
    const stored = localStorage.getItem(getSidebarFolderStorageKey(userUuid))
    const parsed = parseSidebarFolderState(stored)
    if (parsed) return parsed

    const legacy = readLegacySidebarFolderPreference()
    if (legacy) {
      writeSidebarFolderPreference(userUuid, legacy)
      localStorage.removeItem(LEGACY_SIDEBAR_FOLDER_STORAGE_KEY)
      return legacy
    }

    return null
  } catch {
    return null
  }
}

export function writeSidebarFolderPreference(
  userUuid: string | undefined,
  state: SidebarFolderState
): void {
  if (!userUuid || typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.setItem(getSidebarFolderStorageKey(userUuid), JSON.stringify(state))
  } catch {
    // Ignore quota / privacy mode errors
  }
}
